import type { identifier } from '@core/models/base';
import { authedAxios, deleteReq, get, patch, post } from '@shared/axios';
import type { PagedParameters, PagedResult } from '@shared/models/paged.collection';

export const useSimpleFetchRestService = <TDisplay, TIdentifier = identifier>(urlBase: string) => {
  const fetchPaged = async (params: PagedParameters) =>
    (await authedAxios.get<PagedResult<TDisplay>>(urlBase, { params })).data;

  const fetchAll = async (type?: string) => {
    if (type) {
      return await get<TDisplay[]>(`${urlBase}?type=${type}`);
    } else {
      return await get<TDisplay[]>(urlBase);
    }
  };

  const fetchSingle = async (id: TIdentifier, params?: any) => await get<TDisplay>(`${urlBase}/${id}`, params);
  return {
    fetchSingle,
    fetchAll,
    fetchPaged,
    urlBase,
  };
};

export const useSimpleDeleteRestService = <TIdentifier = identifier>(urlBase: string) => {
  const deleteItem = async (id: TIdentifier) => await deleteReq(`${urlBase}/${id}`);
  return {
    deleteItem,
    urlBase,
  };
};

export const useSimpleRestService = <TDisplay, TCreate, TUpdate, TIdentifier = identifier>(urlBase: string) => {
  const { fetchSingle, fetchAll, fetchPaged } = useSimpleFetchRestService<TDisplay, TIdentifier>(urlBase);
  const { deleteItem } = useSimpleDeleteRestService<TIdentifier>(urlBase);

  const update = async (id: TIdentifier, data: Partial<TUpdate>) => await patch<TDisplay>(`${urlBase}/${id}`, data);
  const create = async (data: TCreate) => await post<TDisplay>(`${urlBase}`, data);
  const createOrUpdate = async (id: TIdentifier | undefined, data: TCreate | TUpdate) => {
    if (id === undefined) return create(data as TCreate);
    else return update(id, data as TUpdate);
  };
  return {
    fetchSingle,
    fetchAll,
    fetchPaged,
    create,
    update,
    deleteItem,
    createOrUpdate,
    urlBase,
  };
};
